import { PharosGlobalConfiguration } from '@klareospace/pharos-lib-types';

/**
 * Convert a string to kebab-case. Strips special characters, collapses whitespace
 * and dashes, and trims leading/trailing dashes.
 */
export function toKebabCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Compute up to two initials from a display name.
 * Splits on whitespace, takes the first character of each word,
 * uppercases, and slices to at most 2 characters.
 *
 * @param name - The display name to extract initials from.
 * @param fallback - Returned when name is empty/blank (defaults to '??').
 * @returns The computed initials or the fallback.
 *
 * @example computeInitials('John Doe')     // 'JD'
 * @example computeInitials('Alice')        // 'A'
 * @example computeInitials('', 'N/A')      // 'N/A'
 * @example computeInitials('  ')           // '??'
 */
export function computeInitials(name: string, fallback: string = '??'): string {
  if (!name?.trim()) {
    return fallback;
  }

  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Build a display-ready full name from first and last name parts.
 * Filters out empty/blank values and trims whitespace so that
 * a missing `lastName` never produces a trailing space.
 *
 * @example getFullName('John', 'Doe')  // 'John Doe'
 * @example getFullName('Alice', '')    // 'Alice'
 * @example getFullName('', '')         // ''
 */
export function getFullName(firstName?: string, lastName?: string): string {
  return [firstName, lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Extract a display name from a user-like object.
 * Returns `undefined` when both fields are missing/blank so callers
 * can fall through to an alternative (e.g. Firebase display name).
 *
 * @example getDisplayName({ firstName: 'John', lastName: 'Doe' }) // 'John Doe'
 * @example getDisplayName({ firstName: 'Alice' })                 // 'Alice'
 * @example getDisplayName(undefined)                              // undefined
 * @example getDisplayName({ firstName: '', lastName: '' })        // undefined
 */
export function getDisplayName(user?: { firstName?: string; lastName?: string }): string | undefined {
  if (!user) return undefined;
  const full = getFullName(user.firstName, user.lastName);
  return full || undefined;
}

/** Full display name for a driver-like object, or undefined when no driver is assigned. */
export function resolveDriverName(driver?: { firstName?: string; lastName?: string } | null): string | undefined {
  return getDisplayName(driver ?? undefined);
}

/**
 * Escapes regex metacharacters in user input and returns a RegExp (case-insensitive by default).
 */
export function buildEscapedRegExp(value: string, caseInsensitive = true): RegExp {
  return new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), caseInsensitive ? 'i' : '');
}

const REGEX_METACHARACTER = /[.*+?^${}()|[\]\\]/;

/** Latin letters mapped to their lowercase accented variants — the single source of truth. */
const ACCENT_CLASSES: Record<string, string> = {
  a: 'aàáâãäåāăą',
  c: 'cçćĉċč',
  e: 'eèéêëēĕėęě',
  i: 'iìíîïĩīĭįı',
  n: 'nñńņňŉ',
  o: 'oòóôõöøōŏő',
  s: 'sśŝşš',
  u: 'uùúûüũūŭůűų',
  y: 'yýÿŷ',
  z: 'zźżž',
};

/**
 * Per-base-letter character class covering BOTH lowercase and uppercase accented variants,
 * derived from `ACCENT_CLASSES` via `toUpperCase()` rather than a hand-maintained second table.
 * Explicit uppercase members are required because the `$regex` match ultimately runs inside
 * MongoDB's PCRE2 engine, which (unlike JS) does not reliably case-fold non-ASCII letters under
 * the `i` flag alone.
 */
const ACCENT_CLASSES_CASED: Record<string, string> = Object.fromEntries(
  Object.entries(ACCENT_CLASSES).map(([base, lowerChars]) => [base, lowerChars + lowerChars.toUpperCase()]),
);

/** Reverse lookup: any accented (or base) character -> its base letter in ACCENT_CLASSES. */
const ACCENT_TO_BASE = new Map<string, string>(
  Object.entries(ACCENT_CLASSES).flatMap(([base, accentedChars]) =>
    Array.from(accentedChars).map((char): [string, string] => [char, base]),
  ),
);

/**
 * Escapes regex metacharacters like `buildEscapedRegExp`, then replaces every mapped Latin
 * letter (accented or not) with a character class spanning all its accented variants in both
 * cases, so the resulting RegExp matches regardless of accents or case on either side (query or
 * target) — including when evaluated by MongoDB's PCRE2 `$regex` engine, not just by JS. Always
 * case-insensitive. Accent folding assumes the stored/target value is NFC-normalized; the input
 * `value` itself is normalized to NFC before matching so a decomposed (NFD) query still folds.
 *
 * @example buildAccentInsensitiveRegExp('leon').test('Léon')  // true
 * @example buildAccentInsensitiveRegExp('léon').test('Leon')  // true
 */
export function buildAccentInsensitiveRegExp(value: string): RegExp {
  const pattern = Array.from(value.normalize('NFC'))
    .map((char) => buildAccentFoldedCharPattern(char))
    .join('');
  return new RegExp(pattern, 'i');
}

/** Builds the regex fragment for a single character: escaped metachar, accent class, or itself. */
function buildAccentFoldedCharPattern(char: string): string {
  if (REGEX_METACHARACTER.test(char)) {
    return `\\${char}`;
  }
  const base = ACCENT_TO_BASE.get(char.toLowerCase());
  return base ? `[${ACCENT_CLASSES_CASED[base]}]` : char;
}

/** Trims and lowercases an email so formatting-only differences compare equal. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Whether `email` belongs to one of the internal (Klareo) domains in `PharosGlobalConfiguration.auth.internalEmailDomains`. */
export function isInternalEmail(email: string): boolean {
  const normalized = normalizeEmail(email);
  return PharosGlobalConfiguration.auth.internalEmailDomains.some((domain) => normalized.endsWith(domain));
}

/**
 * Convert a kebab-case or snake_case string to a human-readable title.
 * Splits on hyphens and underscores, capitalises each word.
 *
 * @example humanize('fleet-management') // 'Fleet Management'
 * @example humanize('TENANT_ADMIN')     // 'Tenant Admin'
 * @example humanize('read')             // 'Read'
 */
export function humanize(value: string): string {
  return value
    .split(/[-_]/)
    .map((word) => {
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * Join an array of strings into a single string using the given separator.
 * Returns an empty string when the array is undefined or empty.
 *
 * @param values - The array of strings to join. Handles undefined gracefully.
 * @param separator - The separator placed between each value (defaults to ', ').
 * @returns The joined string, or '' when values is undefined or empty.
 *
 * @example joinStrings(['fragile', 'express'])        // 'fragile, express'
 * @example joinStrings(['a', 'b'], ' | ')             // 'a | b'
 * @example joinStrings([])                             // ''
 * @example joinStrings(undefined)                      // ''
 */
export function joinStrings(values: string[] | undefined, separator: string = ', '): string {
  if (values === undefined || values.length === 0) return '';
  return values.join(separator);
}

const DEFAULT_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const NANOID_ALPHABET = 'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict';
const SHORT_ID_LENGTH = 21;

interface CryptoApi {
  getRandomValues(array: Uint8Array): Uint8Array;
  randomUUID(): string;
}

/** Returns the Web Crypto API (Node.js 19+, modern browsers, RN w/ polyfill) if available. */
function getCryptoApi(): CryptoApi | undefined {
  return typeof globalThis !== 'undefined' && 'crypto' in globalThis
    ? (globalThis as unknown as { crypto: CryptoApi }).crypto
    : undefined;
}

/**
 * Generate a random string of the requested length from the given alphabet.
 * Uses `globalThis.crypto.getRandomValues` for cryptographic-quality randomness
 * when available, falling back to `Math.random` for environments without the
 * Web Crypto API.
 *
 * This function is isomorphic — it does NOT use `node:crypto` and can be
 * bundled for browser or mobile targets.
 *
 * @param length - Number of characters to generate.
 * @param alphabet - Character set to draw from (defaults to A-Z a-z 0-9).
 * @returns The randomly generated string.
 *
 * @example generateRandomString(32)
 * // 'Xk9mLpQr2sTuVwYzAb3cDeFgHiJkNoPq'
 * @example generateRandomString(8, '0123456789')
 * // '48271053'
 */
export function generateRandomString(length: number, alphabet: string = DEFAULT_ALPHABET): string {
  const cryptoApi = getCryptoApi();

  let result = '';
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(length));
    for (let i = 0; i < length; i++) result += alphabet[bytes[i] % alphabet.length];
  } else {
    // Fallback for environments without crypto (rare)
    for (let i = 0; i < length; i++) result += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return result;
}

/** Generate a UUID v4 string, using `crypto.randomUUID` when available. */
export function generateId(): string {
  const cryptoApi = getCryptoApi();
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  // Fallback: simple UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Generate a 21-character URL-safe random string (nanoid-style). */
export function generateShortId(): string {
  return generateRandomString(SHORT_ID_LENGTH, NANOID_ALPHABET);
}

/** Builds the `<prefix>:<uuid>` correlation-id shape. */
export function buildCorrelationId(prefix: string): string {
  return `${prefix}:${generateId()}`;
}
